import { useMutation, useQuery } from "@tanstack/react-query";
import { validate } from "uuid";

import InvestmentClient from "@equidefi/portals/clients/InvestmentClient";
import PaymentClient from "@equidefi/portals/clients/PaymentClient";
import { useApi } from "@equidefi/portals/hooks/useApi";
import { useAuth } from "@equidefi/portals/hooks/useAuth";

import { REINVEST_STATUSES } from "@/constants/investment";
import { useMemo } from "react";
import InvestorInvestmentClient from "../clients/InvestmentClient";
import StripeClient from "../clients/StripeClient";

/**
 * @param {string} id
 * @param {import("@tanstack/react-query").QueryObserverOptions} options
 */
export const useInvestment = (id, options = {}) => {
  const investmentApi = useApi(InvestmentClient);

  const validUuid = validate(id);

  return useQuery(["investment", id], () => investmentApi.get(id), {
    enabled: validUuid,
    ...options,
  });
};

export const useSetupManualInvestmentPayment = (investmentId, options = {}) => {
  const paymentApi = useApi(PaymentClient);

  return useMutation(
    (data) =>
      paymentApi.setupManualPayment({
        ...data,
        investment_id: investmentId,
      }),
    options,
  );
};

/**
 * @param {Record<"offering_id" | "vault_id", string>} params
 * @param {import("@tanstack/react-query").QueryObserverOptions?} options
 */
export const useUserInvestments = (params, options = {}) => {
  const investmentApi = useApi(InvestmentClient);
  const { isLoggedIn } = useAuth();

  return useQuery(
    ["userInvestments", params],
    () => investmentApi.all(params),
    {
      enabled: isLoggedIn,
      ...options,
    },
  );
};

export const useInvestmentInquiries = (investmentId, options = {}) => {
  const investmentApi = useApi(InvestmentClient);

  return useQuery(
    ["investment", investmentId, "inquiry"],
    () => investmentApi.inquiries(investmentId),
    {
      enabled: !!investmentId,
      ...options,
    },
  );
};

/**
 * @param {string} id
 * @param {"accreditation" | "investor-info"} stage
 * @param {import("@tanstack/react-query").UseMutationOptions} options
 */
export const useInvestmentUpdateStage = (id, stage, options = {}) => {
  return useMutation(
    ({ data } = {}) => InvestorInvestmentClient.updateStage(id, stage, data),
    options,
  );
};

export const useUpdateInvestmentSubscription = (id, options = {}) => {
  const investmentApi = useApi(InvestmentClient);
  return useMutation(
    (data) => investmentApi.updateSubscription(id, data),
    options,
  );
};

/**
 * @param {string} investmentId
 * @param {import("@tanstack/react-query").UseMutationOptions} options
 */
export const useInitiatePaymentMutation = (investmentId, options = {}) => {
  return useMutation(
    () => StripeClient.createPaymentIntent(investmentId),
    options,
  );
};

const buildAny =
  (investments) =>
  (mapper = () => {}) => {
    return Number(investments?.filter(mapper)?.length) > 0;
  };

export const canReinvestWith = (investments = []) => {
  if (!investments || !investments[0]) return false;

  const any = buildAny(investments);

  const anyPending = any((investment) => investment.status === "PENDING");
  const anyUnderReview = any(
    (investment) =>
      investment.status === "REVIEW" &&
      (investment.next === "DILIGENCE" || investment.expedited),
  );

  if (anyPending) return false;
  if (anyUnderReview) return true;

  return any((investment) => REINVEST_STATUSES.includes(investment.status));
};

export const useCanReinvest = (investments) => {
  return useMemo(() => canReinvestWith(investments), [investments]);
};

export const useEventDispatcher = () => {
  return useMutation(
    async ({ event, action, investmentId, actor, ...options }) =>
      InvestorInvestmentClient.dispatchEvent(investmentId, {
        event,
        action,
        actor,
        ...options,
      }),
  );
};

/**
 * @param {string} investmentId
 * @param {import("@tanstack/react-query").UseMutationOptions} options
 */
export const useProcessStripePayment = (investmentId, options = {}) => {
  const stripeApi = useApi(StripeClient);
  return useMutation(
    /**
     * @param {string} paymentId
     */
    (paymentId) => stripeApi.processPayment(investmentId, paymentId),
    options,
  );
};

/**
 * @param {string} investmentId
 * @param {import("@tanstack/react-query").UseMutationOptions} options
 */
export const useCancelUncompletePayments = (investmentId, options = {}) => {
  const stripeApi = useApi(StripeClient);
  return useMutation(() => stripeApi.cancelUncompleted(investmentId), options);
};
